import { useAuth } from "@dl/web-core-js/auth";
import { Button, cn, Input, Segmented, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, Info, Search, Star, Truck as TruckIcon, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../../../api/manufacturing";
import { fetchSingleRow, SETTINGS_PATHS, settingsKeys, type PurchasingSettingsDTO } from "../../../api/settings";
import {
  ReplenishmentClient,
  replenishmentKeys,
  fillLabel,
  inOrderUnits,
  snapToBundle,
  type ReplenishmentLineDTO,
  type ReplenishmentSummary,
  type SharedUnitGauge,
} from "../../../api/replenishment";
import { SupplierLink } from "../../../components/SupplierLink";
import { isControl, isTyping } from "./ReplenishmentPage";
import { formatCad, formatCover, formatQuantity, ZoneBadge, ZoneBar } from "./shared";

/**
 * What the latest run says to order, grouped the way it is bought.
 *
 * <p>By supplier, every material with something left to order sits under the
 * supplier it comes from; by truck, materials that travel together share one
 * card with the truck's gauge, and the rest stay under their supplier. Each
 * quantity starts at the run's suggestion and can be changed; whatever is
 * typed is rounded up to the bundle and the minimum order when the field is
 * left, because that is what the supplier will ship anyway.</p>
 *
 * <p>Selected lines become draft orders, one per supplier and warehouse. The
 * run is then redone, so the drafted quantities drop out of this list.</p>
 */

type GroupBy = "supplier" | "truck";
type ZoneFilter = "all" | "red" | "yellow";

interface Group {
  key: string;
  title: string;
  subtitle: string;
  /** The supplier the title (or, for a truck, the subtitle) names. */
  supplierId: number | null;
  truck: SharedUnitGauge | null;
  lines: ReplenishmentLineDTO[];
}

export function ToOrderTab({
  summary,
  explaining,
  onExplain,
}: {
  summary: ReplenishmentSummary | undefined;
  explaining: number | null;
  onExplain: (partId: number | null, warehouseId?: number | null) => void;
}) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new ReplenishmentClient(http), [http]);
  const [groupBy, setGroupBy] = useState<GroupBy>("supplier");
  const [zone, setZone] = useState<ZoneFilter>("all");
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState<Map<number, number>>(new Map());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [focus, setFocus] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const lines = useQuery({
    queryKey: [...replenishmentKeys.lines, "to-order", summary?.replenishment_run_id ?? 0],
    queryFn: () => fetchToOrder(client),
    enabled: summary !== undefined,
  });

  // A new run means new suggestions: whatever was typed against the old ones
  // no longer applies.
  useEffect(() => {
    setQuantities(new Map());
    setSelected(new Set());
    setFocus(0);
  }, [summary?.replenishment_run_id]);

  const quantityOf = useCallback(
    (line: ReplenishmentLineDTO) => quantities.get(line.id) ?? Number(line.order_quantity),
    [quantities],
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (lines.data ?? []).filter((line) => {
      if (zone === "red" && line.zone !== "RED") return false;
      if (zone === "yellow" && line.zone !== "YELLOW") return false;
      if (!needle) return true;
      return (
        line.part_number.toLowerCase().includes(needle) ||
        line.part_name.toLowerCase().includes(needle) ||
        (line.supplier_name ?? "").toLowerCase().includes(needle)
      );
    });
  }, [lines.data, zone, search]);

  const groups = useMemo(() => group(visible, groupBy, summary?.shared_units ?? [], t), [visible, groupBy, summary?.shared_units, t]);
  const flat = useMemo(() => groups.flatMap((g) => g.lines), [groups]);
  const focused = flat[Math.min(focus, flat.length - 1)];

  const toggle = useCallback((ids: number[], on?: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      const turnOn = on ?? ids.some((id) => !next.has(id));
      for (const id of ids) {
        if (turnOn) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const setQuantity = useCallback((id: number, value: number | null) => {
    setQuantities((current) => {
      const next = new Map(current);
      if (value === null) next.delete(id);
      else next.set(id, value);
      return next;
    });
  }, []);

  const chosen = useMemo(
    () => (lines.data ?? []).filter((line) => selected.has(line.id) && quantityOf(line) > 0),
    [lines.data, selected, quantityOf],
  );
  const chosenTotal = chosen.reduce((sum, line) => sum + amountCad(line, quantityOf(line)), 0);
  const chosenSuppliers = new Set(chosen.map((line) => line.supplier_id)).size;

  const draft = useMutation({
    mutationFn: async () => {
      const result = await client.drafts({
        lines: chosen.map((line) => ({
          part_id: line.part_id,
          quantity: quantityOf(line),
          supplier_id: line.supplier_id,
          warehouse_id: line.warehouse_id,
        })),
      });
      // Drafts are deducted by the next run; redo it now so this list is true.
      // The drafts exist whatever happens to the rerun, so its failure is
      // reported apart rather than as if nothing had been created.
      let rerun = true;
      try {
        await client.run();
      } catch {
        rerun = false;
      }
      return { ...result, rerun };
    },
    onSuccess: (result) => {
      if (!result.rerun) {
        toast.warning(
          t("replenishment.draft.rerun-failed", "The drafts are created, but the list could not be recalculated: press Recalculate before ordering more."),
        );
      }
      toast.success(
        t("replenishment.draft.done", "{{count}} draft orders created. Check them in Drafts, then issue.", {
          count: result.orders.length,
        }),
        {
          // One order per line: a plain "\n" would be collapsed into one run of text.
          description: (
            <span className="whitespace-pre-line">
              {result.orders
                .map((order) => `#${String(order.number)} ${order.supplier_name} · ${String(order.line_count)} ${t("replenishment.draft.lines", "lines")}`)
                .join("\n")}
            </span>
          ),
        },
      );
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: replenishmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["purchasing", "purchase-order"] });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  // Row keys. The page owns 1–6, R, ? and Esc.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event)) {
        if (event.key === "Escape") (event.target as HTMLElement).blur();
        return;
      }
      // A focused button or link keeps Enter and Space: they press it.
      if ((event.key === "Enter" || event.key === " ") && isControl(event)) return;
      const line = flat[Math.min(focus, flat.length - 1)];
      switch (event.key) {
        case "ArrowDown":
        case "j":
          event.preventDefault();
          setFocus((index) => Math.min(flat.length - 1, index + 1));
          break;
        case "ArrowUp":
        case "k":
          event.preventDefault();
          setFocus((index) => Math.max(0, index - 1));
          break;
        case " ":
        case "x":
          if (line) {
            event.preventDefault();
            toggle([line.id]);
          }
          break;
        case "Enter":
        case "e":
          if (line) {
            event.preventDefault();
            document.getElementById(`qty-${String(line.id)}`)?.focus();
          }
          break;
        case "i":
          if (line) {
            event.preventDefault();
            onExplain(explaining === line.part_id ? null : line.part_id, line.warehouse_id);
          }
          break;
        case "g":
          if (line) {
            event.preventDefault();
            const owner = groups.find((g) => g.lines.some((l) => l.id === line.id));
            if (owner) toggle(owner.lines.map((l) => l.id));
          }
          break;
        case "d":
          if (chosen.length > 0 && canWrite && !draft.isPending) {
            event.preventDefault();
            draft.mutate();
          }
          break;
        case "/":
          event.preventDefault();
          searchRef.current?.focus();
          break;
        default:
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flat, focus, groups, toggle, onExplain, explaining, chosen.length, canWrite, draft]);

  useEffect(() => {
    if (!focused) return;
    listRef.current?.querySelector(`[data-line="${String(focused.id)}"]`)?.scrollIntoView({ block: "nearest" });
  }, [focused]);

  if (summary && !summary.replenishment_run_id) {
    return <Empty title={t("replenishment.empty.no-run", "No run yet")} body={t("replenishment.empty.no-run.body", "Press Recalculate to plan every purchased material.")} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line-soft bg-surface px-4 py-2 lg:px-6">
        <div className="relative min-w-48 flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
          <Input
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("replenishment.search", "Part, name or supplier  ( / )")}
            className="pl-8"
            aria-label={t("replenishment.search.label", "Search materials")}
          />
        </div>
        <Segmented<ZoneFilter>
          value={zone}
          onChange={setZone}
          options={[
            { value: "all", label: t("replenishment.filter.all", "All") },
            { value: "red", label: t("replenishment.filter.red", "Red only") },
            { value: "yellow", label: t("replenishment.filter.yellow", "Order point") },
          ]}
        />
        <Segmented<GroupBy>
          value={groupBy}
          onChange={setGroupBy}
          options={[
            { value: "supplier", label: t("replenishment.group.supplier", "By supplier") },
            { value: "truck", label: t("replenishment.group.shared", "By truck or container") },
          ]}
        />
        <span className="ml-auto text-[12px] text-ink-muted max-md:hidden">
          {t("replenishment.hint.keys", "Press ? for keyboard shortcuts")}
        </span>
      </div>

      <div ref={listRef} className="@container min-h-0 flex-1 overflow-auto bg-ground px-2 py-3 sm:px-4 lg:px-6">
        {lines.isError ? (
          <p className="m-4 rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("replenishment.error", "The suggestions could not be loaded.")}
          </p>
        ) : !summary || lines.isPending ? (
          // Until the summary names the run, the lines are not even asked for:
          // "Nothing to order" here would be a guess.
          <p className="p-6 text-[13px] text-ink-muted">{t("table.loading", "Loading…")}</p>
        ) : groups.length === 0 ? (
          <Empty
            icon={<CheckCircle2 className="size-8 text-ok" />}
            title={
              (lines.data ?? []).length === 0
                ? t("replenishment.empty.nothing", "Nothing to order")
                : t("replenishment.empty.filtered", "Nothing matches")
            }
            body={
              (lines.data ?? []).length === 0
                ? t("replenishment.empty.nothing.body", "Every planned material is above its order point, or already drafted.")
                : t("replenishment.empty.filtered.body", "Clear the search or the zone filter.")
            }
          />
        ) : (
          <div className="flex flex-col gap-3 pb-20">
            {groups.map((g) => (
              <GroupCard
                key={g.key}
                group={g}
                quantityOf={quantityOf}
                selected={selected}
                focusedId={focused?.id ?? null}
                explaining={explaining}
                canWrite={canWrite}
                onToggle={toggle}
                onFocus={(id) => setFocus(flat.findIndex((line) => line.id === id))}
                onQuantity={setQuantity}
                onExplain={onExplain}
              />
            ))}
          </div>
        )}
      </div>

      {chosen.length > 0 && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line bg-surface-over px-4 py-3 shadow-raised lg:px-6">
          <span className="text-[13px] text-ink">
            <strong className="tabular-nums">{chosen.length}</strong>{" "}
            {t("replenishment.selection.materials", "materials")} ·{" "}
            <strong className="tabular-nums">{chosenSuppliers}</strong>{" "}
            {t("replenishment.selection.suppliers", "suppliers")}
          </span>
          <span className="text-[15px] font-semibold tabular-nums text-ink">{formatCad(chosenTotal)}</span>
          <ApprovalHint totals={supplierTotals(chosen, quantityOf)} />
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" onClick={() => setSelected(new Set())}>
              <X className="size-4" />
              {t("replenishment.selection.clear", "Clear")}
            </Button>
            <Button variant="primary" disabled={!canWrite} loading={draft.isPending} onClick={() => draft.mutate()} title="D">
              {t("replenishment.selection.draft", "Create draft orders")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- data --- */

/** Every line with something left to order, a thousand at a time. */
async function fetchToOrder(client: ReplenishmentClient): Promise<ReplenishmentLineDTO[]> {
  const rows: ReplenishmentLineDTO[] = [];
  for (let page = 0; page < 20; page++) {
    const result = await client.find({ page, size: 1000, filters: { order_quantity: ["0.0001", ""] }, total: page === 0 });
    rows.push(...result.content);
    if (result.content.length < 1000) break;
  }
  return rows;
}

/** CAD for a quantity, from the run's own CAD amount so the exchange rate agrees with the server. */
function amountCad(line: ReplenishmentLineDTO, quantity: number): number {
  const suggested = Number(line.order_quantity);
  if (suggested > 0 && line.order_amount_cad !== null) return (Number(line.order_amount_cad) / suggested) * quantity;
  return Number(line.unit_price ?? 0) * quantity;
}

function group(
  lines: ReplenishmentLineDTO[],
  by: GroupBy,
  trucks: SharedUnitGauge[],
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): Group[] {
  const byKey = new Map<string, Group>();
  for (const line of lines) {
    const truck =
      by === "truck" && line.order_unit_kind === "SHARED" && line.order_unit_id
        ? trucks.find((x) => x.order_unit_id === line.order_unit_id) ?? null
        : null;
    const key = truck ? `truck:${String(truck.order_unit_id)}` : `supplier:${String(line.supplier_id ?? "none")}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = {
        key,
        title: truck ? `${truck.code} · ${truck.name}` : line.supplier_name ?? t("replenishment.no-supplier", "No supplier"),
        subtitle: truck
          ? truck.supplier_name
          : t("replenishment.group.lead", "{{days}}-day lead time", { days: line.lead_time_days }),
        supplierId: truck ? truck.supplier_id : line.supplier_id,
        truck,
        lines: [],
      };
      byKey.set(key, entry);
    }
    entry.lines.push(line);
  }
  const urgency = (g: Group) => Math.min(...g.lines.map((l) => (l.zone === "RED" ? 0 : 1) + Math.max(-1, Math.min(1, Number(l.buffer_level ?? 1))) / 10));
  // By truck, the trucks come first — that is what the view is for — then
  // everything else by urgency.
  const truckFirst = (g: Group) => (by === "truck" && g.truck ? 0 : 1);
  return [...byKey.values()].sort(
    (a, b) => truckFirst(a) - truckFirst(b) || urgency(a) - urgency(b) || a.title.localeCompare(b.title),
  );
}

/* ------------------------------------------------------------- pieces --- */

function GroupCard({
  group: g,
  quantityOf,
  selected,
  focusedId,
  explaining,
  canWrite,
  onToggle,
  onFocus,
  onQuantity,
  onExplain,
}: {
  group: Group;
  quantityOf: (line: ReplenishmentLineDTO) => number;
  selected: Set<number>;
  focusedId: number | null;
  explaining: number | null;
  canWrite: boolean;
  onToggle: (ids: number[], on?: boolean) => void;
  onFocus: (id: number) => void;
  onQuantity: (id: number, value: number | null) => void;
  onExplain: (partId: number | null, warehouseId?: number | null) => void;
}) {
  const { t } = useTranslation();
  const ids = g.lines.map((line) => line.id);
  const all = ids.every((id) => selected.has(id));
  const some = !all && ids.some((id) => selected.has(id));
  const total = g.lines.reduce((sum, line) => sum + amountCad(line, quantityOf(line)), 0);
  const red = g.lines.filter((line) => line.zone === "RED").length;
  const load = g.truck ? g.lines.reduce((sum, line) => sum + quantityOf(line) * Number(line.load_per_stock_unit ?? 1), 0) : 0;

  return (
    <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-soft px-3 py-2.5 sm:px-4">
        <input
          type="checkbox"
          className="size-5 accent-[var(--accent)]"
          checked={all}
          ref={(el) => {
            if (el) el.indeterminate = some;
          }}
          onChange={() => onToggle(ids, !all)}
          aria-label={t("replenishment.group.select", "Select every material of {{name}}", { name: g.title })}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <h3 className="flex items-center gap-2 truncate text-[14px] font-semibold text-ink">
            {g.truck && <TruckIcon className="size-4 shrink-0 text-ink-muted" />}
            {g.truck ? g.title : <SupplierLink id={g.supplierId} className="truncate">{g.title}</SupplierLink>}
          </h3>
          <p className="truncate text-[12px] text-ink-muted">
            {g.truck ? <SupplierLink id={g.supplierId}>{g.subtitle}</SupplierLink> : g.subtitle} · {t("replenishment.group.count", "{{count}} materials", { count: g.lines.length })}
            {red > 0 && <span className="text-risk"> · {t("replenishment.group.red", "{{count}} in red", { count: red })}</span>}
          </p>
        </div>
        {g.truck && <TruckGauge truck={g.truck} load={load} />}
        <span className="text-[14px] font-semibold tabular-nums text-ink">{formatCad(total)}</span>
      </header>
      <div role="list">
        {g.lines.map((line) => (
          <LineRow
            key={line.id}
            line={line}
            quantity={quantityOf(line)}
            isSelected={selected.has(line.id)}
            isFocused={focusedId === line.id}
            isExplained={explaining === line.part_id}
            canWrite={canWrite}
            onToggle={() => onToggle([line.id])}
            onFocus={() => onFocus(line.id)}
            onQuantity={(value) => onQuantity(line.id, value)}
            onExplain={() => onExplain(explaining === line.part_id ? null : line.part_id, line.warehouse_id)}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * One material. Wide screens read it as a table row; a tablet gets the same
 * facts on two lines, with the checkbox, the quantity and the explain button
 * kept large enough for a finger.
 */
function LineRow({
  line,
  quantity,
  isSelected,
  isFocused,
  isExplained,
  canWrite,
  onToggle,
  onFocus,
  onQuantity,
  onExplain,
}: {
  line: ReplenishmentLineDTO;
  quantity: number;
  isSelected: boolean;
  isFocused: boolean;
  isExplained: boolean;
  canWrite: boolean;
  onToggle: () => void;
  onFocus: () => void;
  onQuantity: (value: number | null) => void;
  onExplain: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState<string | null>(null);
  const suggested = Number(line.order_quantity);
  const edited = quantity !== suggested;
  const unit = line.stock_unit_code ?? "";
  const after = Number(line.position_quantity) + quantity;

  const commit = () => {
    if (text === null) return;
    // A cleared field means "back to the suggestion", not zero: zero is typed as 0.
    if (text.trim() === "") {
      onQuantity(null);
      setText(null);
      return;
    }
    const typed = Number(text.replace(",", "."));
    const snapped = Number.isFinite(typed) ? snapToBundle(typed, line.order_multiple, line.minimum_order_quantity) : suggested;
    onQuantity(snapped === suggested ? null : snapped);
    setText(null);
  };

  return (
    <div
      role="listitem"
      data-line={line.id}
      onClick={onFocus}
      className={cn(
        "grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 border-b border-line-soft px-3 py-2 last:border-b-0 sm:px-4",
        "@5xl:grid-cols-[2.25rem_4.5rem_minmax(12rem,1.6fr)_minmax(8rem,1fr)_4.5rem_8.5rem_9rem_6.5rem_2.5rem]",
        isSelected && "bg-accent-soft/40",
        isFocused && "outline outline-2 -outline-offset-2 outline-accent/60",
        isExplained && "bg-info-soft/40",
      )}
    >
      <input
        type="checkbox"
        className="size-5 accent-[var(--accent)] @max-5xl:row-span-2"
        checked={isSelected}
        onChange={onToggle}
        onClick={(event) => event.stopPropagation()}
        aria-label={t("replenishment.row.select", "Select {{part}}", { part: line.part_number })}
      />
      <div className="@max-5xl:hidden">
        <ZoneBadge zone={line.zone} />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="@5xl:hidden">
            <ZoneBadge zone={line.zone} />
          </span>
          <Link
            to="/manufacturing/parts/$partId"
            params={{ partId: String(line.part_id) }}
            onClick={(event) => event.stopPropagation()}
            className="truncate font-mono text-[13px] font-semibold text-ink hover:text-accent"
          >
            {line.part_number}
          </Link>
          {line.is_strategic && (
            <Star className="size-3.5 shrink-0 fill-warn text-warn" aria-label={t("replenishment.strategic", "Feeds top sellers")} />
          )}
        </div>
        <p className="truncate text-[12px] text-ink-muted" title={line.part_name}>
          {line.part_name}
        </p>
      </div>
      <div className="flex flex-col gap-1 @max-5xl:col-span-2 @max-5xl:col-start-2 @max-5xl:row-start-2">
        <ZoneBar
          red={Number(line.red_quantity)}
          minimum={Number(line.minimum_quantity)}
          maximum={Number(line.maximum_quantity)}
          position={Number(line.position_quantity)}
          after={after}
        />
        <span className="text-[11px] tabular-nums text-ink-faint">
          {formatQuantity(line.position_quantity)} → {formatQuantity(after)} / {formatQuantity(line.maximum_quantity)} {unit}
        </span>
      </div>
      <div className="text-right text-[12px] tabular-nums text-ink-muted @max-5xl:hidden" title={t("replenishment.row.cover", "Days of cover")}>
        {formatCover(line.days_of_cover === null ? null : Number(line.days_of_cover), t)}
      </div>
      <div className="text-[12px] leading-tight tabular-nums text-ink-muted @max-5xl:hidden">
        <div>
          {t("replenishment.row.on-hand", "on hand")} <span className="text-ink">{formatQuantity(line.on_hand_quantity)}</span>
        </div>
        {Number(line.on_order_quantity) > 0 && (
          <div>
            {t("replenishment.row.on-order", "on order")} <span className="text-ink">+{formatQuantity(line.on_order_quantity)}</span>
          </div>
        )}
        {Number(line.reserved_quantity) > 0 && (
          <div>
            {t("replenishment.row.reserved", "reserved")} <span className="text-ink">−{formatQuantity(line.reserved_quantity)}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-0.5 @max-5xl:row-start-1 @max-5xl:col-start-3" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center gap-1">
          <Input
            id={`qty-${String(line.id)}`}
            inputMode="decimal"
            disabled={!canWrite}
            value={text ?? String(quantity)}
            onChange={(event) => setText(event.target.value)}
            onFocus={(event) => {
              onFocus();
              event.target.select();
            }}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commit();
                (event.target as HTMLInputElement).blur();
              }
            }}
            className={cn("h-9 w-24 text-right font-semibold tabular-nums", edited && "border-accent text-accent")}
            aria-label={t("replenishment.row.quantity", "Quantity to order of {{part}}", { part: line.part_number })}
          />
          <span className="w-8 truncate text-[11px] text-ink-faint">{unit}</span>
        </div>
        {inOrderUnits(quantity, line, formatQuantity) && (
          <span className="text-[11px] tabular-nums text-ink-muted">= {inOrderUnits(quantity, line, formatQuantity)}</span>
        )}
        {line.closure_reason && (
          <span
            className="max-w-48 truncate text-[11px] text-warn"
            title={t("replenishment.row.closure.title", "{{reason}}: closed {{start}} to {{end}}", {
              reason: line.closure_reason,
              start: line.closure_start_date ?? "",
              end: line.closure_end_date ?? "",
            })}
          >
            {line.order_by_date
              ? t("replenishment.row.order-by", "order by {{date}}: {{reason}}", { date: line.order_by_date, reason: line.closure_reason })
              : t("replenishment.row.delayed", "arrives {{date}}: {{reason}}", { date: line.arrival_date ?? "", reason: line.closure_reason })}
          </span>
        )}
        <span className="text-[11px] text-ink-faint">
          {edited ? (
            <button type="button" className="text-accent hover:underline" onClick={() => onQuantity(null)}>
              {t("replenishment.row.reset", "reset to {{value}}", { value: formatQuantity(suggested) })}
            </button>
          ) : line.order_unit_kind === "PER_PART" && line.order_unit_code ? (
            t("replenishment.row.order-unit", "{{unit}} of {{value}}", { unit: line.order_unit_code, value: formatQuantity(line.order_multiple) })
          ) : line.order_multiple ? (
            t("replenishment.row.bundle", "bundle of {{value}}", { value: formatQuantity(line.order_multiple) })
          ) : line.minimum_order_quantity ? (
            t("replenishment.row.moq", "min. {{value}}", { value: formatQuantity(line.minimum_order_quantity) })
          ) : Number(line.drafted_quantity) > 0 ? (
            t("replenishment.row.drafted", "{{value}} already drafted", { value: formatQuantity(line.drafted_quantity) })
          ) : null}
        </span>
      </div>
      <div className="text-right text-[13px] font-medium tabular-nums text-ink @max-5xl:col-start-3 @max-5xl:row-start-2">
        {!(Number(line.unit_price ?? 0) > 0) && !(Number(line.order_amount_cad ?? 0) > 0) ? (
          // CA$0 would read as free; the supplier simply has no price on file
          // (35 imported links carry 0, none carry a real free price).
          <span className="text-[12px] font-normal text-warn" title={t("replenishment.row.no-price.hint", "No price on file for this supplier: the total leaves it out.")}>
            {t("replenishment.row.no-price", "no price")}
          </span>
        ) : (
          formatCad(amountCad(line, quantity))
        )}
      </div>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onExplain();
        }}
        className={cn(
          "flex size-9 items-center justify-center rounded-md text-ink-muted hover:bg-surface-sunk hover:text-ink @max-5xl:hidden",
          isExplained && "bg-info-soft text-info",
        )}
        aria-label={t("replenishment.row.explain", "Why this quantity?")}
        title={t("replenishment.row.explain", "Why this quantity?") + " (i)"}
      >
        <Info className="size-4" />
      </button>
      {line.note && (
        <p className="col-span-full text-[11px] italic text-ink-muted @5xl:col-start-3">{line.note}</p>
      )}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onExplain();
        }}
        className="col-span-full -mt-1 text-left text-[12px] font-medium text-accent @5xl:hidden"
      >
        {t("replenishment.row.explain", "Why this quantity?")}
      </button>
    </div>
  );
}

/**
 * A shared unit's load against its capacity: ticks at the fill levels the
 * supplier sells (or at the minimum load without them), and the level the
 * load is billed at.
 */
function TruckGauge({ truck, load }: { truck: SharedUnitGauge; load: number }) {
  const { t } = useTranslation();
  const capacity = Number(truck.capacity);
  const levels = truck.fill_levels.map(Number);
  const ticks = levels.length > 0 ? levels : capacity > 0 ? [Number(truck.minimum_load) / capacity] : [];
  // With fill levels any load is billed at a level: only a minimum load can leave it short.
  const lowest = levels.length > 0 ? 0 : Number(truck.minimum_load);
  const share = capacity > 0 ? Math.min(1, load / capacity) : 0;
  const over = load > capacity;
  const under = load > 0 && load < lowest;
  const billed = levels.find((level) => load <= level * capacity + 1e-9) ?? null;
  return (
    <div
      className="flex w-56 flex-col gap-1"
      title={
        levels.length > 0
          ? t("replenishment.truck.title.levels", "Load against capacity; the ticks are the fill levels the supplier sells")
          : t("replenishment.truck.title", "Load against capacity; the tick is the minimum load")
      }
    >
      <div className="relative h-2.5 overflow-hidden rounded-full bg-surface-sunk">
        <div
          className={cn("absolute inset-y-0 left-0 rounded-full", over ? "bg-risk" : under ? "bg-warn" : "bg-ok")}
          style={{ width: `${String(share * 100)}%` }}
        />
        {ticks
          .filter((tick) => tick > 0 && tick < 1)
          .map((tick) => (
            <div key={tick} className="absolute inset-y-0 w-0.5 bg-ink/60" style={{ left: `${String(tick * 100)}%` }} />
          ))}
      </div>
      <span className={cn("text-[11px] tabular-nums", over ? "text-risk" : under ? "text-warn" : "text-ink-muted")}>
        {formatQuantity(load)} / {formatQuantity(capacity)} {truck.load_unit_label}
        {over
          ? ` · ${t("replenishment.truck.over", "over capacity")}`
          : under
            ? ` · ${t("replenishment.truck.under", "below the minimum")}`
            : billed != null && load > 0
              ? ` · ${t("replenishment.truck.billed", "billed {{fill}}", { fill: fillLabel(billed, t) })}`
              : truck.is_urgent
                ? ` · ${t("replenishment.truck.urgent", "urgent")}`
                : ""}
      </span>
    </div>
  );
}

/** Each supplier's total in a selection: one draft order each. */
function supplierTotals(lines: ReplenishmentLineDTO[], quantityOf: (line: ReplenishmentLineDTO) => number): number[] {
  const totals = new Map<number | null, number>();
  for (const line of lines) totals.set(line.supplier_id, (totals.get(line.supplier_id) ?? 0) + amountCad(line, quantityOf(line)));
  return [...totals.values()];
}

/** Warns before the click which of the orders about to be drafted will wait for the approver once issued. */
function ApprovalHint({ totals }: { totals: number[] }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const settings = useQuery({
    queryKey: settingsKeys.purchasing,
    queryFn: () => fetchSingleRow<PurchasingSettingsDTO>(http, SETTINGS_PATHS.purchasing),
    staleTime: 5 * 60_000,
  });
  const threshold = settings.data ? Number(settings.data.approval_threshold_amount) : null;
  const large = threshold === null ? 0 : totals.filter((total) => total >= threshold).length;
  if (threshold === null || large === 0) return null;
  return (
    <span className="rounded-md bg-info-soft px-2 py-0.5 text-[12px] text-info">
      {t("replenishment.selection.approval", "{{count}} at or above {{amount}}: approval needed once issued", {
        count: large,
        amount: formatCad(threshold),
      })}
    </span>
  );
}

function Empty({ icon, title, body }: { icon?: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      {icon}
      <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
      <p className="max-w-sm text-[13px] text-ink-muted">{body}</p>
    </div>
  );
}
